import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {
  DeviceActionTypes,
  GetOrganizationDevices,
  GetOrganizationDevicesComplete,
  GetOrganizationDevicesError,
  PostDeviceNames,
  PostDeviceNamesComplete,
  PostDeviceNamesError,
  PostHideDevicesLed,
  PostHideDevicesLedComplete,
  PostHideDevicesLedError,
  PostShowDevicesLed,
  PostShowDevicesLedComplete,
  PostShowDevicesLedError
} from '../actions/device.actions';
import {catchError, exhaustMap, map} from 'rxjs/operators';
import {of} from 'rxjs';
import {
  Device,
  DeviceSetting,
  GetOrganizationDevicesRequest,
  PostDeviceBrightnessesRequest,
  PostDeviceNamesRequest
} from '../models/device';
import {DeviceService} from '../../core/index';
import {HttpErrorResponse} from '@angular/common/http';
import {Simple200Response} from '../models/general';
import {mergeMap} from 'rxjs/internal/operators';

@Injectable()
export class DeviceEffects {

  @Effect()
  getDevices$ = this.actions$.pipe(
    ofType(DeviceActionTypes.GetOrganizationDevices),
    map((action: GetOrganizationDevices) => action.payload),
    exhaustMap((getOrgDevicesRequest: GetOrganizationDevicesRequest) =>
      this.deviceService.getOrganizationDevices(getOrgDevicesRequest)
        .pipe(
          map((devices: Device[]) => new GetOrganizationDevicesComplete(devices)),
          catchError((error: HttpErrorResponse) => of(new GetOrganizationDevicesError({
            errorMessage: error.error.message
          })))
        )
    )
  );

  @Effect()
  postDeviceNames$ = this.actions$.pipe(
    ofType(DeviceActionTypes.PostDeviceNames),
    map((action: PostDeviceNames) => action.payload),
    exhaustMap((postDeviceNamesRequest: PostDeviceNamesRequest) =>
      this.deviceService.postDeviceSettings(postDeviceNamesRequest)
        .pipe(
          mergeMap((response: Simple200Response) => [
            new GetOrganizationDevices({organizationToken: postDeviceNamesRequest.organizationToken}),
            new PostDeviceNamesComplete()
          ]),
          catchError((error: HttpErrorResponse) => of(new PostDeviceNamesError({
            errorMessage: error.status.toString()
          })))
        )
    )
  );

  @Effect()
  postShowDevicesLed$ = this.actions$.pipe(
    ofType(DeviceActionTypes.PostShowDevicesLed),
    map((action: PostShowDevicesLed) => action.payload),
    exhaustMap(({organizationToken, organizationId, deviceUuids}: PostDeviceBrightnessesRequest) => {
      const deviceSettings: DeviceSetting[] = deviceUuids.reduce((acc: DeviceSetting[], deviceUuid: string) => {
        return [
          ...acc,
          {
            deviceUuid,
            brightness: 100
          }
        ];
      }, []);
      return this.deviceService.postDeviceSettings({organizationToken, organizationId, deviceSettings})
        .pipe(
          map((response: Simple200Response) => new PostShowDevicesLedComplete()),
          catchError((error: HttpErrorResponse) => of(new PostShowDevicesLedError({
            errorMessage: error.status.toString()
          })))
        )
    })
  );

  @Effect()
  postHideDevicesLed$ = this.actions$.pipe(
    ofType(DeviceActionTypes.PostHideDevicesLed),
    map((action: PostHideDevicesLed) => action.payload),
    exhaustMap(({organizationToken, organizationId, deviceUuids}: PostDeviceBrightnessesRequest) => {
      const deviceSettings: DeviceSetting[] = deviceUuids.reduce((acc: DeviceSetting[], deviceUuid: string) => {
        return [
          ...acc,
          {
            deviceUuid,
            brightness: 0
          }
        ];
      }, []);
      return this.deviceService.postDeviceSettings({organizationToken, organizationId, deviceSettings})
        .pipe(
          map((response: Simple200Response) => new PostHideDevicesLedComplete()),
          catchError((error: HttpErrorResponse) => of(new PostHideDevicesLedError({
            errorMessage: error.status.toString()
          })))
        )
    })
  );

  constructor(private actions$: Actions,
              private deviceService: DeviceService) {
  }
}
